import Head from 'next/head';
import { useRouter } from 'next/router';
import { staticAssetUrl } from 'remote-com-shared/domains/assets/helpers';
import { defaultLocale } from 'remote-com-shared/domains/locales/config';
import { qualifiedLinkResolver } from 'remote-com-shared/domains/navigation/hrefResolver';
import { getCanonicalURL } from 'remote-com-shared/style/helpers';

const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || '';
const basePath = process.env.NEXT_PUBLIC_BASE_PATH || '';

const defaultImage = {
  url: staticAssetUrl('/images/social-remote-employ.png'),
  alt: null,
  dimensions: {
    width: 1200,
    height: 630,
  },
};

function getOrgSchemaStructuredData() {
  return {
    '@context': 'https://www.schema.org',
    '@type': 'Organization',
    name: 'Remote',
    url: 'https://remote.com',
    sameAs: [
      'https://www.instagram.com/remote',
      'https://twitter.com/remote',
      'https://www.linkedin.com/company/remote.com',
    ],
    logo: 'https://remote.com/blog/images/new_logo.svg',
    description:
      'We help companies of all sizes hire top talent all over the world, in full compliance with local laws.',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94133',
      addressCountry: 'United States Of America',
    },
  };
}

export function DocumentHead({ document = {}, noIndex = false, orgSchema = false }) {
  const router = useRouter();

  const normalizedDocument = document || {};
  const {
    seo_title: title,
    seo_description: description,
    social_image: sourceImage,
    _meta: meta,
  } = normalizedDocument;

  const currentPageUrl = meta
    ? qualifiedLinkResolver(meta)
    : `${baseUrl}${basePath}${router?.asPath}`;

  const canonicalHref = getCanonicalURL(currentPageUrl);

  const alternateLangs = meta && [
    ...meta.alternateLanguages,
    {
      lang: meta.lang,
      uid: meta.uid,
      type: meta.type,
    },
  ];

  const alternateHrefs = alternateLangs
    ?.map((alternate) => ({
      href: getCanonicalURL(qualifiedLinkResolver(alternate)),
      lang: alternate.lang,
      key: `alternate-${alternate.lang}`,
    }))
    .concat([
      alternateLangs.some((locale) => locale.lang === defaultLocale)
        ? {
            href: getCanonicalURL(
              qualifiedLinkResolver(alternateLangs.find((locale) => locale.lang === defaultLocale))
            ),
            lang: 'x-default',
            key: `alternate-x-default`,
          }
        : {
            href: canonicalHref,
            lang: 'x-default',
            key: `alternate-x-default`,
          },
    ]);

  const { url, alt, dimensions: { width, height } = {} } = sourceImage || defaultImage;

  const normalizedTitle = title || 'Global HR Solutions for Distributed Teams';
  const normalizedDescription =
    description ||
    'Hire international talent in minutes. Remote is the most disruptive global payroll, tax, HR and compliance solution for distributed teams.';
  return (
    <Head>
      {normalizedTitle && <title>{normalizedTitle} | Remote</title>}
      {normalizedDescription && (
        <meta name="description" content={normalizedDescription} key="description" />
      )}
      {noIndex && <meta name="robots" content="noindex,follow" key="robots" />}

      {canonicalHref && <link key="canonical" rel="canonical" href={canonicalHref} />}
      {alternateHrefs?.map(({ lang, href, key }) => (
        <link key={key} rel="alternate" hrefLang={lang} href={href} />
      ))}

      {canonicalHref && <meta property="og:url" content={canonicalHref} key="og:url" />}
      {normalizedTitle && <meta property="og:title" content={normalizedTitle} key="og:title" />}
      {normalizedDescription && (
        <meta property="og:description" content={normalizedDescription} key="og:description" />
      )}
      {canonicalHref && <meta property="og:type" content="website" key="og:type" />}
      {url && <meta property="og:image" content={url} key="og:image" />}
      {url && <meta property="og:image:url" content={url} key="og:image:url" />}
      {alt && <meta property="og:image:alt" content={alt} key="og:image:alt" />}
      {width && <meta property="og:image:width" content={width} key="og:image:width" />}
      {height && <meta property="og:image:height" content={height} key="og:image:height" />}

      {normalizedTitle && (
        <meta name="twitter:title" content={normalizedTitle} key="twitter:title" />
      )}
      {normalizedDescription && (
        <meta
          name="twitter:description"
          content={normalizedDescription}
          key="twitter:description"
        />
      )}
      {url && <meta property="twitter:image" content={url} key="twitter:image" />}
      {alt && <meta property="twitter:image:alt" content={alt} key="twitter:image:alt" />}
      <meta name="twitter:card" content="summary_large_image" key="twitter:card" />
      <meta name="twitter:site" content="@remote" key="twitter:site" />

      {orgSchema && (
        <script
          key="org-schema"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(getOrgSchemaStructuredData()),
          }}
        />
      )}
    </Head>
  );
}
